import { Box, Divider, Stack, Typography } from "@mui/material";
import { icons } from "assets/AssetHelper";
import * as dayjs from "dayjs";
import { useTranslation } from "react-i18next";

const labelSx = { color: "#969696", fontSize: { xs: "13px", sm: "14px" } };
const valueSx = { fontSize: { xs: "14px", sm: "16px" }, fontWeight: 500 };

function ColorSwatch({ presentation, name }) {
  if (!name && !presentation) return null;
  return (
    <Stack direction="row" alignItems="center" gap={1}>
      {presentation && (
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: 1,
            bgcolor: presentation,
            border: "1px solid #e0e0e0",
          }}
        />
      )}
      {name && <Typography sx={valueSx}>{name}</Typography>}
    </Stack>
  );
}

function PriceLine({ price, priceAfterDiscount, currency }) {
  if (price == null && priceAfterDiscount == null) return null;
  const showBoth =
    price != null &&
    priceAfterDiscount != null &&
    String(price) !== String(priceAfterDiscount);

  return (
    <Typography sx={valueSx} color="primary">
      {showBoth ? (
        <>
          <Typography
            component="span"
            sx={{
              textDecoration: "line-through",
              color: "text.secondary",
              mr: 1,
              fontSize: "inherit",
            }}
          >
            {price} {currency}
          </Typography>
          {priceAfterDiscount} {currency}
        </>
      ) : (
        <>
          {priceAfterDiscount ?? price} {currency}
        </>
      )}
    </Typography>
  );
}

function FieldRow({ label, children }) {
  if (children == null || children === false) return null;
  return (
    <Stack gap={0.5}>
      <Typography sx={labelSx}>{label}</Typography>
      {children}
    </Stack>
  );
}

function PackagingEntry({ entry, cartItems, index, totalCount }) {
  const { t } = useTranslation();
  const currency = entry?.currency;

  const wrappingTypeLabel =
    entry?.wrapping_type === "ready"
      ? t("orders.wrapping_type_ready")
      : entry?.wrapping_type === "custom"
        ? t("orders.wrapping_type_custom")
        : entry?.wrapping_type;

  const scopeLabel =
    entry?.scope === "order"
      ? t("orders.packaging_scope_order")
      : entry?.scope === "item"
        ? t("orders.packaging_scope_item")
        : entry?.scope;

  const linkedItem =
    entry?.scope === "item" && entry?.cart_item_id
      ? cartItems?.find((item) => item?.id === entry.cart_item_id)
      : null;
  const itemLabel =
    linkedItem?.variant?.name ||
    (entry?.cart_item_id
      ? `#${entry.cart_item_id}`
      : null);

  const suggested = entry?.suggested_wrapper;
  const wrapImage =
    entry?.wrap_variant?.image || entry?.wrap?.image || suggested?.image;

  return (
    <Stack gap={2}>
      {totalCount > 1 && (
        <Typography fontWeight={600} fontSize={{ xs: "15px", sm: "18px" }}>
          {t("orders.packaging_entry")} {index + 1}
        </Typography>
      )}

      <Stack direction="row" gap={3} flexWrap="wrap" alignItems="flex-start">
        {wrapImage && (
          <Box
            component="img"
            src={wrapImage}
            alt=""
            sx={{
              width: 80,
              height: 80,
              borderRadius: 2,
              objectFit: "cover",
            }}
          />
        )}

        <Stack gap={1.5} flex={1} minWidth={200}>
          <Stack direction={{ xs: "column", sm: "row" }} gap={3} flexWrap="wrap">
            <FieldRow label={t("orders.packaging_scope")}>
              <Typography sx={valueSx}>
                {scopeLabel}
                {itemLabel ? ` — ${itemLabel}` : ""}
              </Typography>
            </FieldRow>

            <FieldRow label={t("orders.type")}>
              <Typography sx={valueSx}>{wrappingTypeLabel}</Typography>
            </FieldRow>

            {entry?.captured_at && (
              <FieldRow label={t("orders.packaging_captured_at")}>
                <Typography sx={valueSx}>
                  {dayjs(entry.captured_at).format("DD-MM-YYYY, hh:mm a")}
                </Typography>
              </FieldRow>
            )}
          </Stack>
        </Stack>
      </Stack>

      {suggested && (
        <Stack gap={1}>
          <Typography sx={labelSx}>
            {t("orders.packaging_suggested_wrapper")}
          </Typography>
          <Stack direction="row" gap={2} alignItems="flex-start">
            {suggested.image && (
              <Box
                component="img"
                src={suggested.image}
                alt={suggested.name}
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 1,
                  objectFit: "cover",
                }}
              />
            )}
            <Stack gap={0.5}>
              <Typography sx={valueSx}>{suggested.name}</Typography>
              {suggested.description && (
                <Typography variant="body2" color="text.secondary">
                  {suggested.description}
                </Typography>
              )}
              <PriceLine
                price={suggested.cost}
                priceAfterDiscount={suggested.cost_after_discount}
                currency={currency}
              />
            </Stack>
          </Stack>
        </Stack>
      )}

      <Stack direction="row" gap={4} flexWrap="wrap">
        {entry?.wrap && (
          <FieldRow label={t("orders.wrap")}>
            <Stack direction="row" alignItems="center" gap={1}>
              {entry.wrap.image && (
                <Box
                  component="img"
                  src={entry.wrap.image}
                  alt={entry.wrap.name}
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    objectFit: "cover",
                  }}
                />
              )}
              <Typography sx={valueSx}>{entry.wrap.name}</Typography>
            </Stack>
          </FieldRow>
        )}

        {entry?.shape && (
          <FieldRow label={t("orders.packaging_shape")}>
            <Typography sx={valueSx}>{entry.shape.name}</Typography>
          </FieldRow>
        )}

        {entry?.wrap_variant && (
          <FieldRow label={t("orders.label_wrap_variant")}>
            <Stack gap={0.5}>
              <Typography sx={valueSx}>{entry.wrap_variant.name}</Typography>
              <PriceLine
                price={entry.wrap_variant.price}
                priceAfterDiscount={entry.wrap_variant.price_after_discount}
                currency={currency}
              />
            </Stack>
          </FieldRow>
        )}

        {entry?.color && (
          <FieldRow label={t("orders.color_of_wrap")}>
            <ColorSwatch
              presentation={entry.color.presentation}
              name={entry.color.name}
            />
          </FieldRow>
        )}

        {entry?.ribbon_color && (
          <FieldRow label={t("orders.label_ribbon")}>
            <Stack gap={0.5}>
              <ColorSwatch
                presentation={entry.ribbon_color.presentation}
                name={entry.ribbon_color.name}
              />
              <PriceLine
                price={entry.ribbon_color.price}
                priceAfterDiscount={entry.ribbon_color.price_after_discount}
                currency={currency}
              />
            </Stack>
          </FieldRow>
        )}
      </Stack>

      {entry?.personal_touches?.length > 0 && (
        <Stack gap={1}>
          <Typography sx={labelSx}>
            {t("orders.packaging_personal_touches")}
          </Typography>
          <Stack gap={1.5}>
            {entry.personal_touches.map((touch) => (
              <Stack
                key={touch.id}
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                gap={0.5}
              >
                <Stack gap={0.25}>
                  <Typography sx={valueSx}>{touch.name}</Typography>
                  {touch.custom_text && (
                    <Typography variant="body2" color="text.secondary">
                      {t("orders.packaging_custom_text")}: {touch.custom_text}
                    </Typography>
                  )}
                </Stack>
                <PriceLine
                  price={touch.price}
                  priceAfterDiscount={touch.price_after_discount}
                  currency={currency}
                />
              </Stack>
            ))}
          </Stack>
        </Stack>
      )}

      {entry?.decorative_extras?.length > 0 && (
        <Stack gap={1}>
          <Typography sx={labelSx}>
            {t("orders.packaging_decorative_extras")}
          </Typography>
          <Stack gap={1.5}>
            {entry.decorative_extras.map((extra) => (
              <Stack
                key={extra.id}
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                gap={0.5}
              >
                <Typography sx={valueSx}>{extra.name}</Typography>
                <PriceLine
                  price={extra.price}
                  priceAfterDiscount={extra.price_after_discount}
                  currency={currency}
                />
              </Stack>
            ))}
          </Stack>
        </Stack>
      )}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        gap={1}
      >
        <Typography sx={labelSx}>{t("orders.packaging_total")}</Typography>
        <PriceLine
          price={entry?.cost}
          priceAfterDiscount={entry?.cost_after_discount}
          currency={currency}
        />
      </Stack>
    </Stack>
  );
}

export default function OrderPackagingInfo({ packaging, cartItems }) {
  const { t } = useTranslation();

  if (!packaging?.length) return null;

  return (
    <Stack
      sx={{
        backgroundColor: "white",
        p: 3,
        gap: 3,
        borderRadius: "8px",
      }}
    >
      <Stack direction="row" alignItems="center" gap={2}>
        <Box component="img" width="46px" src={icons.wrappers} alt="" />
        <Typography fontWeight="500" fontSize={{ xs: "18px", sm: "24px" }}>
          {t("orders.packaging_selection")}
        </Typography>
      </Stack>

      {packaging.map((entry, index) => (
        <Stack key={entry.id ?? index} gap={3}>
          {index > 0 && <Divider />}
          <PackagingEntry
            entry={entry}
            cartItems={cartItems}
            index={index}
            totalCount={packaging.length}
          />
        </Stack>
      ))}
    </Stack>
  );
}
